// Display chrome for the reading-entry panels, keyed by the identifier the UI already uses to
// pick one (`SelectedPatientAdministration`'s `adhocReadingOptions`, passed through
// `AdhocReadingModal`'s `readingType` prop).
//
// The `type`/`unit` pair - the part that gets persisted and matched by the backend - comes from
// services/readings.js, which mirrors kraken's `App\Support\ReadingTypes`. Only presentation
// lives here; placeholders and fraction digits have no business in the registry.

import { BGL_READING, INR_READING } from '@/services/readings';

export const READING_PANELS = {
    bgl: {
        ...BGL_READING,
        heading: 'Blood Glucose Reading',
        inputLabel: "Enter Patient's BGL (mmols/L)",
        inputTooltip: 'BGL Reading',
        placeholder: 'e.g. 5.5',
        minFractionDigits: 0,
        maxFractionDigits: 1,
        submitLabel: 'Submit BGL',
        successPrefix: 'BGL',
        successSuffix: 'mmols/L',
        errorFallback: 'Failed to save BGL.',
    },
    inr: {
        ...INR_READING,
        heading: 'INR Reading',
        inputLabel: "Enter Patient's INR",
        inputTooltip: 'INR Reading',
        placeholder: 'e.g. 2.5',
        minFractionDigits: 1,
        maxFractionDigits: 1,
        submitLabel: 'Submit INR',
        successPrefix: 'INR',
        helpText: 'International Normalized Ratio ranges typically between 2.0 and 3.0 for basic anticoagulant therapy.',
        errorFallback: 'Failed to save INR.',
    },
    creatinine: {
        type: 'Serum Creatinine',
        unit: 'µmol/L',
        heading: 'Serum Creatinine Reading',
        inputLabel: "Enter Patient's Serum Creatinine (µmol/L)",
        inputTooltip: 'Serum Creatinine Reading',
        placeholder: 'e.g. 90',
        minFractionDigits: 0,
        maxFractionDigits: 1,
        submitLabel: 'Submit Reading',
        successPrefix: 'Serum Creatinine',
        successSuffix: 'µmol/L',
        errorFallback: 'Failed to save Serum Creatinine reading.',
        // Serum creatinine is reported in µmol/L by Australian pathology labs, which is the unit
        // stored above. Anything consuming this reading in mg/dL - the warfarin dosing calculator
        // in PrescriptionChart - is responsible for converting; see MICROMOL_PER_L_TO_MG_PER_DL
        // there.
    },
    sedation: {
        type: 'Sedation',
        unit: 'Score',
        heading: 'Sedation Assessment Tool (SAT)',
        headerTooltip: 'Australian Standard Sedation Score (-3 to +3)',
        inputLabel: "Select Resident's Sedation Level",
        submitLabel: 'Submit SAT Score',
        successPrefix: 'SAT Score',
        errorFallback: 'Failed to save sedation score.',
        // The scale is signed and 0 is a routine value ("awake and calm"), so it is the one
        // reading where the display has to keep the sign.
        formatValue: value => (value > 0 ? `+${value}` : `${value}`),
    },
};

// The Australian Standard Sedation Score. Only SedationPanel uses it, but it sits with the rest
// of the panel configuration rather than in that component's data().
export const SAT_SCORES = [
    { value: 3, label: 'Very agitated / violent' },
    { value: 2, label: 'Agitated' },
    { value: 1, label: 'Anxious or restless' },
    { value: 0, label: 'Awake and calm' },
    { value: -1, label: 'Rouses to voice' },
    { value: -2, label: 'Rouses to physical stimulation' },
    { value: -3, label: 'Unrousable' },
];
